import { useEffect, useMemo, useRef, useState } from 'react';
import { addDays, format } from 'date-fns';

import * as S from './FeaturedActivities.styles';
import { SliderDots } from '@/common/ui';
import { Container } from '@/common/ui/containers/Container';
import { useCarousel } from '@/common/app/hooks/useCarousel';
import { IRecommendsItem } from './featuredActivitiesPreview/FeaturedActivitiesPreview.types';
import { NewFeaturedActivitiesPreview } from './featuredActivitiesPreview/NewFeaturedActivitiesPreview';
import { useMedia } from '@/common/service/hooks/useMedia';
import { shuffle } from '@/common/app/hooks/useRecommended';
import { useSearchContext } from '@/common/app/contexts';
import { SERVER_FORMAT_DATE } from '@/common/app/constants/timeConstants';
import { useCacheAvailabilityStore, isTripAvailable } from '../store/cacheAvailabilityStore';

export type FeaturedActivitiesProps = {
    defaultActivities: IRecommendsItem[];
    title?: string;
    subTitle?: string;
    item_list_id: string;
    item_list_name: string;
    placeholders: {
        [key: string]: string;
    };
    isPriority?: boolean;
};

export const FeaturedCached = ({
    defaultActivities,
    title = '',
    subTitle,
    item_list_name,
    item_list_id,
    placeholders,
    isPriority,
}: FeaturedActivitiesProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const isDesktop = useMedia('(min-width: 992px)');

    const [recommended, setRecommended] = useState(shuffle(defaultActivities, 12));
    const { selectedDateRange } = useSearchContext();
    const { availabilityMap, fetchAvailability } = useCacheAvailabilityStore();

    const defaultToday = useRef(new Date());
    const effectiveDateRange = useMemo(
        () =>
            selectedDateRange ?? {
                dateStart: defaultToday.current,
                dateEnd: addDays(defaultToday.current, 1),
            },
        [selectedDateRange]
    );

    useEffect(() => {
        const from = format(effectiveDateRange.dateStart, SERVER_FORMAT_DATE);
        const to = format(effectiveDateRange.dateEnd, SERVER_FORMAT_DATE);
        const ids = recommended.map((i) => i.id);
        fetchAvailability(ids, from, to, isPriority);
    }, [effectiveDateRange, recommended, fetchAvailability, isPriority]);

    useEffect(() => {
        setRecommended(shuffle(defaultActivities, 12));
    }, [defaultActivities]);

    const displayList = useMemo(() => {
        const from = format(effectiveDateRange.dateStart, SERVER_FORMAT_DATE);
        const to = format(effectiveDateRange.dateEnd, SERVER_FORMAT_DATE);
        return recommended.filter((item) => {
            const record = availabilityMap[item.id];
            if (!record) return true;
            return isTripAvailable(availabilityMap, item.id, from, to);
        });
    }, [recommended, availabilityMap, effectiveDateRange]);

    const {
        prevBtnIsActive,
        nextBtnIsActive,
        onClickHandler,
        onScrollHandler,
        DIRECTION,
        withDot,
        dotActive,
    } = useCarousel({
        ref: scrollRef,
        countItems: displayList.length || 4,
    });

    const isMoreElements = displayList.length > 1;

    if (!defaultActivities.length) {
        return <></>;
    }

    return (
        <S.Wrapper>
            <Container>
                <S.Block data-test-id="Widget-attraction" className="container">
                    {Boolean(title) && (
                        <S.Header>
                            <S.LeftPartHeader>
                                <S.Title>{title}</S.Title>
                                {subTitle && <S.SubTitle>{subTitle}</S.SubTitle>}
                            </S.LeftPartHeader>
                        </S.Header>
                    )}
                    <S.CarouselWraper>
                        <S.Gallery ref={scrollRef} onScroll={onScrollHandler}>
                            <S.InnerContainer className="carousel-container">
                                {displayList.map((slide, index) => (
                                    <NewFeaturedActivitiesPreview
                                        {...slide}
                                        numberOfItems={displayList.length}
                                        position={index}
                                        key={slide.id}
                                        listName={title}
                                        item_list_id={item_list_id}
                                        item_list_name={item_list_name}
                                        promotion_name={title}
                                        placeholder={
                                            placeholders?.[slide.id] || slide.placeholder || ''
                                        }
                                        isDesktop={isDesktop}
                                        isWebApp={false}
                                        isBlur={!availabilityMap[slide.id]}
                                    />
                                ))}
                            </S.InnerContainer>
                        </S.Gallery>
                        {prevBtnIsActive && withDot && isMoreElements && (
                            <S.CenterLeftArrow
                                onClick={() => onClickHandler(DIRECTION.prev)}
                                disabled={false}
                            />
                        )}
                        {nextBtnIsActive && withDot && isMoreElements && (
                            <S.CenterRightArrow
                                onClick={() => onClickHandler(DIRECTION.next)}
                                disabled={false}
                            />
                        )}
                    </S.CarouselWraper>
                    <S.DotsSlider>
                        {!!withDot &&
                            [...Array(displayList.length).keys()].map((el) => {
                                return (
                                    <SliderDots isActive={el === Math.ceil(dotActive)} key={el} />
                                );
                            })}
                    </S.DotsSlider>
                </S.Block>
            </Container>
        </S.Wrapper>
    );
};
